import axios from 'axios';

// API base URL
export const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Get the effective branch from localStorage (for super admin view-as feature)
const getViewAsBranch = () => {
  const viewAs = localStorage.getItem('viewAsBranch');
  return viewAs || null;
};

// Create axios instance with interceptor
const api = axios.create({
  baseURL: API_BASE,
});

// Request interceptor - adds auth token and view_as_branch to ALL requests
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add view_as_branch parameter for super admin
    const viewAsBranch = getViewAsBranch();
    if (viewAsBranch) {
      // Add to URL params
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}view_as_branch=${viewAsBranch}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Temporary/backend error or dropped connection (network error, request
    // timeout, or a 5xx while the backend is mid-restart/redeploy) — NEVER
    // force a logout for these. Retry once (idempotent GET requests only)
    // after a short delay before surfacing the error to the caller, since
    // these are almost always transient.
    const config = error.config || {};
    const hasResponse = !!error.response;
    const status = error.response?.status;
    const isTransientError = !hasResponse || status >= 500;
    const isRetriableMethod = (config.method || 'get').toLowerCase() === 'get';
    if (isTransientError && isRetriableMethod && !config._retried) {
      config._retried = true;
      await new Promise((resolve) => setTimeout(resolve, 1200));
      try {
        return await api.request(config);
      } catch (retryError) {
        error = retryError;
      }
    }

    // Handle 401 errors (unauthorized) - but NOT for login/auth endpoints, and
    // NOT for "step-up" re-verification endpoints (e.g. re-entering your
    // password to unlock 8x8/Zoho/Samsara credential editing) — a wrong
    // password there is a normal, expected rejection that should just show a
    // toast, not nuke the user's whole active session.
    //
    // Also treat a 403 with detail EXACTLY "Not authenticated" the same way —
    // that's FastAPI's own HTTPBearer response when the Authorization header
    // is completely missing (not a role/permission 403, which uses its own
    // custom detail text and must NOT trigger this). This happens once a
    // session's token has already gone missing (e.g. right after the first
    // 401 above clears it but before the redirect below finishes, so a few
    // other in-flight requests fire with no token at all) — without this,
    // those requests would get stuck showing a raw, unrecoverable
    // "Not authenticated" toast forever instead of being sent to /login.
    //
    // Also exclude 401s with detail EXACTLY "Valid step-up token required" —
    // returned by the actual SAVE/TEST calls on the Zoho/8x8/Samsara
    // credential-editing screens (not just the verify-step-up endpoint
    // itself) whenever that short-lived (5 min) re-auth token has expired or
    // was already used. That's a completely separate, low-stakes concern from
    // the user's real login session and must never log them out of the whole
    // app — it should just prompt them to re-verify and try saving again.
    //
    // Genuine network errors / timeouts (error.response is undefined) and any
    // 5xx never reach this branch at all (status is undefined/<401, or is
    // >=500 and already retried above) — only a REAL 401/403-missing-auth
    // from the backend ever clears the token below.
    const isMissingAuthHeader = error.response?.status === 403 && error.response?.data?.detail === 'Not authenticated';
    const isStaleStepUpToken = error.response?.status === 401 && error.response?.data?.detail === 'Valid step-up token required';
    if ((error.response?.status === 401 || isMissingAuthHeader) && !isStaleStepUpToken) {
      const url = error.config?.url || '';
      // Don't redirect if already on login-related or step-up endpoints
      if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/reset') && !url.includes('/verify-step-up')) {
        localStorage.removeItem('token');
        // Don't clear viewAsBranch - keep it for when user logs back in
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
